import apiClient from '../apiClient';
import type {
  ApiResponse,
  PaginationMeta,
  TopupOrderListItem,
  TopupOrderDetail,
} from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';

const BASE_URL = '/dashboard/topup-orders';

interface PaginatedResponse {
  items: TopupOrderListItem[];
  pagination: PaginationMeta;
}

export const getTopupOrders = async (
  page?: number,
  limit?: number,
  search?: string,
  status?: string,
  companyId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<PaginatedResponse> => {
  const url = getUrlWithQueryParams(BASE_URL, {
    page,
    limit,
    search,
    status,
    companyId,
    dateFrom,
    dateTo,
  });
  const { data } = await apiClient.get<ApiResponse<TopupOrderListItem[]>>(url);
  return { items: data.data, pagination: data.meta! };
};

export const getTopupOrderById = async (id: string): Promise<TopupOrderDetail> => {
  const { data } = await apiClient.get<ApiResponse<TopupOrderDetail>>(`${BASE_URL}/${id}`);
  return data.data;
};

export const retryFiscal = async (topupOrderId: string): Promise<unknown> => {
  const { data } = await apiClient.post<ApiResponse<unknown>>(
    `/payments/fiscal/retry/${topupOrderId}`,
  );
  return data.data;
};

// React Query hooks

export const useGetTopupOrders = (
  page?: number,
  limit?: number,
  search?: string,
  status?: string,
  companyId?: string,
  dateFrom?: string,
  dateTo?: string,
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_TOPUP_ORDERS, page, limit, search, status, companyId, dateFrom, dateTo],
    queryFn: () => getTopupOrders(page, limit, search, status, companyId, dateFrom, dateTo),
  });

export const useGetTopupOrder = (id: string | undefined) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_TOPUP_ORDER, id],
    queryFn: () => getTopupOrderById(id!),
    enabled: !!id,
  });

export const useRetryFiscal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: retryFiscal,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_TOPUP_ORDER, id] });
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_TOPUP_ORDERS] });
    },
  });
};
